import { processTranslationField } from 'app/services/sfAuth/sfData/sfFundingStream'

const languageAPIToKey = {
  English: 'en',
  French: 'fr',
  Inuktitut: 'iu-cans',
  'English/French': ['en', 'fr']
}

const langKeyToAPIValue = {
  en: 'English',
  fr: 'French',
  'iu-cans': 'Inuktitut'
}

export const fundingStreamFieldsMapSF = {
  direction: 'in',
  Name: 'sfName',
  Full_Name__r: {
    key: 'fullName',
    in: (stream) => processTranslationField(stream, 'Full_Name__r', 'fullName')
  },
  Full_Name__c: {
    key: 'fullName',
    out: (res) => (res.fullName ?? null)
  },
  Stream_name_only__r: {
    key: 'name',
    in: (stream) =>
      processTranslationField(
        stream,
        'Stream_name_only__r',
        'nameTranslations'
      )
  },
  Stream_name_only__c: {
    key: 'name',
    out: (res) => (res.name ?? null)
  },
  Id: 'id',
  Funding_Program__r: {
    key: 'subtitle',
    in: (stream, parseObj) => {
      parseObj.fundingProgram = stream.Funding_Program__c
      return processTranslationField(
        stream,
        'Funding_Program__r.Full_name__r',
        'subtitle'
      )
    }
  },
  Opportunity_Record_Type__c: 'applicationRecordType',
  Funding_Program__c: 'fundingProgram',
  Conflict_of_interest_form__c: 'conflictOfInterestForm',
  Short_description__r: {
    key: 'description',
    in: (stream) =>
      processTranslationField(stream, 'Short_description__r', 'description')
  },
  Short_description__c: {
    key: 'description',
    out: (res) => (res.description ?? null)
  },
  Hardcoded_executive_summary_form__c: 'hardcodedExecutiveSummaryForm',
  Eligibility_Quiz__c: 'quiz',
  Application_form__c: 'form',
  Application_print_view__c: 'printForm',
  Application_view_for_hardcoded__c: 'applicationViewForHardcoded',
  Grantee_report_preview__c: 'granteeReportPreviewForm',
  Grantee_Report__c: 'granteeReportForm',
  Grantee_progress_report_survey_template__c:
    'granteeProgressReportSurveyTemplate',
  Fallback_grantee_report_form__c: 'granteeReportFallbackForm',
  Grantee_final_report_survey_template__c: 'granteeFinalReportSurveyTemplate',
  Applications_list_print_preview__c: 'internalPrintPreview',
  Funding_agreement__c: 'contract',
  Selection_Committee_preview__c: 'SC',
  Allocation_Committee_preview__c: 'AC',
  Board_summary_preview__c: 'Board',
  CMHC_summary_preview__c: 'CMHC',
  External_review_application_view__c: 'externalReviewApplicationView',
  External_review_form__c: 'externalReviewForm',
  External_review_retroactive__c: 'externalReviewRetroactive',
  Objective_Record_Type__c: 'objectiveRecordType',
  Disable_in_provinces__c: {
    key: 'disableInProvinces',
    in: (stream) =>
      stream.Disable_in_provinces__c != null
        ? stream.Disable_in_provinces__c.split(';')
        : [],
    out: (res) => res.disableInProvinces.join(';')
  },
  Available_in_languages__c: {
    key: 'avaliableInLanguages',
    in: (stream) => {
      if (stream.Available_in_languages__c == null) return []

      return stream.Available_in_languages__c
        .split(';')
        .flatMap((lang) => languageAPIToKey[lang] ?? [])
        .filter(Boolean)
    },
    out: (res) =>
      res.avaliableInLanguages
        .map((lang) => langKeyToAPIValue[lang])
        .filter((v) => v)
        .join(';')
  },
  Validity_start__c: 'startTime',
  Validity_end__c: 'endTime',
  Reminder_date__c: 'reminderTime',
  Is_in_testing__c: 'inTesting',
  Is_obsolete__c: 'obsolete',
  Filters_Organization_type__c: {
    key: 'organizationType',
    in: (stream) =>
      stream.Filters_Organization_type__c != null
        ? stream.Filters_Organization_type__c.split(';')
        : []
  },
  Filters_Project_location__c: {
    key: 'projectLocation',
    in: (stream) =>
      stream.Filters_Project_location__c != null
        ? stream.Filters_Project_location__c.split(';')
        : []
  },
  Pre_Qualification_Application_Form__c: 'prequalificationForm',
  Pre_Qualification_Eligibility_Quiz__c: 'prequalificationEligibilityQuiz',
  Prequalification_Record_Type__c: 'prequalificationRecordType',
  DMAH_view__c: 'dmahView',
  More_info_url__r: {
    key: 'moreInfoUrl',
    in: (stream) =>
      processTranslationField(stream, 'More_info_url__r', 'moreInfoUrl')
  },
  More_info_url__c: {
    key: 'moreInfoUrl',
    out: (res) => (res.moreInfoUrl ?? null)
  },
  Unique_name__c: 'uniqueName',
  Logo_url__c: 'logo',
  Notes__c: 'notes',
  Order__c: 'order',
  Configuration_Data_Imported__c: 'configurationDataImported',
  Case_type__c: 'caseType',
  Hide_program_name__c: 'hideProgramName',
  Total_Required_Votes__c: 'totalRequiredVotes'
}

export interface FundingStream {
  direction: string
  sfName: string
  fullName: string | null
  name: string | null
  id: string
  subtitle: string | null
  applicationRecordType: string
  fundingProgram: string
  description: string | null
  quiz: string
  form: string
  printForm: string
  applicationViewForHardcoded: string
  granteeReportPreviewForm: string
  granteeReportForm: string
  granteeProgressReportSurveyTemplate: string
  granteeFinalReportSurveyTemplate: string
  granteeReportFallbackForm?: string
  internalPrintPreview: string
  contract: string
  SC: string
  AC: string
  Board: string
  CMHC: string
  externalReviewApplicationView: string
  hardcodedExecutiveSummaryForm?: string
  externalReviewForm: string | null
  externalReviewRetroactive: string | null
  objectiveRecordType: string
  disableInProvinces: string[]
  avaliableInLanguages: string[]
  startTime: string
  endTime: string
  reminderTime: string
  inTesting: boolean
  obsolete: boolean
  organizationType: string[]
  projectLocation: string[]
  prequalificationForm: string
  prequalificationEligibilityQuiz: string
  prequalificationRecordType: string
  conflictOfInterestForm: string
  dmahView: string
  moreInfoUrl: string | null
  uniqueName: string
  logo: string
  notes: string
  order: number
  configurationDataImported: boolean
  caseType: string
  hideProgramName: boolean
  totalRequiredVotes: number
}
